import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { DateTime } from 'luxon';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Button from '../../common/Button';
import FarmacoAutocomplete from '../../common/FarmacoAutocomplete';
import PrescriptionService from '../../../services/prescriptionService';
import PatientService from '../../../services/patientService';
import './NewPrescription.css';
import AppointmentService from '../../../services/appointmentService';
import LocationService from '../../../services/locationService';

const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID (older browsers).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const NewPrescription = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [patient, setPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // One idempotency key per form session. It survives retries caused by
  // network failures so the backend can return the originally-created
  // prescription instead of writing a duplicate. It is intentionally NOT
  // stored in React state — that would let it change on re-render and
  // defeat the purpose.
  const idempotencyKeyRef = useRef(null);
  if (idempotencyKeyRef.current === null) {
    idempotencyKeyRef.current = generateIdempotencyKey();
  }

  const [formData, setFormData] = useState({
    generalNotes: '',
    nextCheckupDate: '',
    medications: [
      {
        farmaco: null, // Store the full farmaco object
        farmacoId: '',
        periodicity: '',
        duration: '',
        notes: ''
      }
    ]
  });

  useEffect(() => {
    if (hasLoadedOnce) return;
    
    const loadData = async () => {
      const token = auth.user?.access_token;
      
      if (!token) {
        setIsLoadingData(false);
        setHasLoadedOnce(true);
        return;
      }
      
      try {
        setIsLoadingData(true);
        
        const patientData = await PatientService.getPatientById(patientId, token);
        setPatient(patientData);
        setIsLoadingData(false);
        setHasLoadedOnce(true);
      } catch (error) {
        console.error('Error loading patient:', error);
        setError('Error al cargar información del paciente');
        setIsLoadingData(false);
        setHasLoadedOnce(true);
      }
    };
    
    loadData();
  }, [hasLoadedOnce, patientId, auth.user?.access_token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map((med, i) => 
        i === index ? { ...med, [field]: value } : med
      )
    }));
  };

  const handleFarmacoSelect = (index, farmaco) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map((med, i) => 
        i === index ? { 
          ...med, 
          farmaco: farmaco,
          farmacoId: farmaco ? farmaco.id : ''
        } : med
      )
    }));
  };

  const addMedication = () => {
    setFormData(prev => ({
      ...prev,
      medications: [
        ...prev.medications,
        {
          farmaco: null,
          farmacoId: '',
          periodicity: '',
          duration: '',
          notes: ''
        }
      ]
    }));
  };

  const removeMedication = (index) => {
    if (formData.medications.length <= 1) return;
    
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    if (formData.medications.some(med => !med.farmaco || !med.periodicity || !med.duration)) {
      setMessage({
        type: 'error',
        text: 'Todos los medicamentos deben tener fármaco, periodicidad y duración'
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    const token = auth.user?.access_token;

    // Prepare prescription data
    const prescriptionData = {
      patientId,
      generalNotes: formData.generalNotes,
      nextCheckupDate: formData.nextCheckupDate || null,
      medications: formData.medications.map(med => ({
        farmacoId: med.farmacoId,
        farmaco: med.farmaco ? {
          id: med.farmaco.id,
          components: med.farmaco.components,
          dosis: med.farmaco.dosis,
          commercialName: med.farmaco.commercialName
        } : null,
        periodicity: med.periodicity,
        duration: med.duration,
        notes: med.notes
      }))
    };

    // Step 1: create the prescription. This is the only step whose failure
    // should be reported as "Error al crear la prescripción". Any failure
    // here is safely retried by the user because the backend deduplicates
    // on the idempotency key sent below.
    let prescriptionResult;
    try {
      prescriptionResult = await PrescriptionService.createPrescription(
        prescriptionData,
        token,
        idempotencyKeyRef.current
      );
    } catch (error) {
      console.error('Error creating prescription:', error);
      setMessage({
        type: 'error',
        text: 'Error al crear la prescripción. Inténtalo de nuevo.'
      });
      setIsLoading(false);
      return;
    }

    const prescriptionId = prescriptionResult.data.id;

    // Step 2: optionally schedule the follow-up appointment. Failures here
    // must NOT roll back the prescription and must NOT be reported as
    // "Error al crear la prescripción" — the prescription was created
    // successfully, the user just needs to know the appointment wasn't.
    if (formData.nextCheckupDate) {
      try {
        const timezone = auth.user?.profile?.zoneinfo || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const appointmentDateTime = DateTime.fromISO(formData.nextCheckupDate, { zone: timezone }).toISO();

        const locationsResponse = await LocationService.getAllLocations(token);
        const locations = locationsResponse.data || [];
        const defaultLocation =
          locations.find((location) => typeof location.name === 'string' && location.name.trim().toLowerCase() === 'consultorio principal') ||
          locations[0];

        if (!defaultLocation?.id) {
          throw new Error('No hay ubicaciones disponibles para programar la cita de control');
        }

        await AppointmentService.createAppointment({
          patientId,
          doctorId: auth.user?.profile.sub,
          locationId: defaultLocation.id,
          appointmentDateTime: appointmentDateTime,
          durationMinutes: 30,
          status: 'scheduled',
          notes: 'Cita de control programada automáticamente desde la prescripción'
        }, token);

        // Prescription and appointment both created. Hand off to the view
        // page and let it surface a success toast.
        navigate(`/patients/${patientId}/prescriptions/${prescriptionId}`, {
          state: {
            message: 'Prescripción creada y cita de control agendada exitosamente',
            messageId: `prescription-and-appointment-created-${prescriptionId}-${Date.now()}`,
            messageType: 'success'
          }
        });
      } catch (appointmentError) {
        console.error('Error scheduling follow-up appointment:', appointmentError);
        // The prescription was created successfully; only the optional
        // follow-up appointment failed. Hand off to the view page and let
        // it surface a warning toast so the user is informed without
        // being stranded on this form.
        navigate(`/patients/${patientId}/prescriptions/${prescriptionId}`, {
          state: {
            message: 'Prescripción creada, pero no se pudo agendar la cita de control. Puedes agendarla manualmente desde la prescripción.',
            messageId: `prescription-created-no-appointment-${prescriptionId}-${Date.now()}`,
            messageType: 'warning'
          }
        });
      }
      setIsLoading(false);
      return;
    }

    // No follow-up appointment to schedule.
    navigate(`/patients/${patientId}/prescriptions/${prescriptionId}`, {
      state: {
        message: 'Prescripción creada exitosamente',
        messageId: `prescription-created-${prescriptionId}-${Date.now()}`,
        messageType: 'success'
      }
    });
    setIsLoading(false);
  };

  const handleCancel = () => {
    navigate(`/patients/${patientId}/prescriptions`);
  };

  if (isLoadingData) {
    return (
      <div className="new-prescription-container">
        <div className="prescriptions-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error && !patient) {
    return (
      <div className="new-prescription-container">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="new-prescription-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Nueva Prescripción"
        onBackClick={() => navigate(`/patients/${patientId}/prescriptions`)}
      />

      <div className="prescription-form-container">
        <form onSubmit={handleSubmit} className="prescription-form">
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Medications Section */}
          <div className="form-section">
            <div className="section-header">
              <h3>Medicamentos</h3>
              <Button
                type="button"
                variant="outline"
                size="small"
                icon="fas fa-plus"
                onClick={addMedication}
              >
                Agregar Medicamento
              </Button>
            </div>

            <div className="medications-list">
              {formData.medications.map((medication, index) => (
                <div key={index} className="medication-form-item">
                  <div className="medication-header">
                    <h4>Medicamento {index + 1}</h4>
                    {formData.medications.length > 1 && (
                      <Button
                        type="button"
                        variant="danger"
                        size="small"
                        icon="fas fa-trash"
                        onClick={() => removeMedication(index)}
                      >
                        Eliminar
                      </Button>
                    )}
                  </div>

                  <div className="medication-form-grid">
                    <div className="form-group">
                      <label htmlFor={`farmaco-${index}`}>Fármaco</label>
                      <FarmacoAutocomplete
                        selectedFarmaco={medication.farmaco}
                        onFarmacoSelect={(farmaco) => handleFarmacoSelect(index, farmaco)}
                        placeholder="Seleccionar fármaco"
                        searchPlaceholder="Buscar fármaco por nombre..."
                        disabled={isLoading}
                        minSearchLength={2}
                        maxResults={50}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`periodicity-${index}`}>Periodicidad</label>
                      <input
                        type="text"
                        id={`periodicity-${index}`}
                        value={medication.periodicity}
                        onChange={(e) => handleMedicationChange(index, 'periodicity', e.target.value)}
                        placeholder="Ej: 3 veces al día, cada 8 horas"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`duration-${index}`}>Duración</label>
                      <input
                        type="text"
                        id={`duration-${index}`}
                        value={medication.duration}
                        onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                        placeholder="Ej: 7 días, 2 semanas"
                        required
                      />
                    </div>

                    <div className="form-group full-width">
                      <label htmlFor={`med-notes-${index}`}>Notas del medicamento</label>
                      <textarea
                        id={`med-notes-${index}`}
                        value={medication.notes}
                        onChange={(e) => handleMedicationChange(index, 'notes', e.target.value)}
                        placeholder="Instrucciones especiales, advertencias, etc."
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              ))}
              {formData.medications.length > 1 && (<div className="section-footer" style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem' }}>
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  icon="fas fa-plus"
                  onClick={addMedication}
                >
                  Agregar Medicamento
                </Button>
              </div>)}
            </div>
          </div>

          {/* General Notes Section */}
          <div className="form-section">
            <h3>Notas Generales (Opcional)</h3>
            <div className="form-group">
              <label htmlFor="generalNotes">Notas de la prescripción</label>
              <textarea
                id="generalNotes"
                name="generalNotes"
                value={formData.generalNotes}
                onChange={handleInputChange}
                placeholder="Información general sobre el tratamiento, diagnóstico, etc."
                rows="4"
              />
            </div>
          </div>

          {/* Next Checkup Section */}
          <div className="form-section">
            <h3>Próxima Cita de Control (Opcional)</h3>
            <div className="form-group">
              <label htmlFor="nextCheckupDate">Fecha de próxima cita</label>
              <input
                type="datetime-local"
                id="nextCheckupDate"
                name="nextCheckupDate"
                value={formData.nextCheckupDate}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              icon="fas fa-save"
            >
              {isLoading ? 'Creando...' : 'Crear Prescripción'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPrescription;
